import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { countdownVisible } from "@/components/TrialCountdown";
import { startCheckout, openBillingPortal, errorMessage } from "@/lib/api";
import { longDate } from "@/lib/time";

function bannerState(user, price) {
  const ends = longDate(user.trial_ends_at);
  const left = `${user.trial_days_left} ${user.trial_days_left === 1 ? "day" : "days"} left`;
  if (user.payment_issue) return { tone: "alert", text: user.payment_locked ? "Your payment didn't go through — update your card to restore access." : "Your last payment didn't go through — update your card to avoid interruption.", action: "Update card", portal: true };
  if (user.trialing) return { tone: "ok", text: user.cancel_at_period_end ? `Free trial · ${left} · your plan is cancelled and ends ${ends}` : `Free trial · ${left} · ${price} starts ${ends}`, link: ["/account", "Manage"] };
  if (user.subscription_status === "active") return null;
  if (user.needs_card) return { tone: "alert", text: "Add a card to start your free trial — no charge until it ends.", action: "Add card" };
  if (!user.trial_active) return { tone: "alert", text: `Your free trial is over — keep exploring with the ${price}.`, link: ["/pricing", "Subscribe now"] };
  return { tone: "ok", text: `Free trial · ${left} — ends ${ends}. Add a card so your service isn't interrupted.`, action: "Add card" };
}

export function TrialBanner() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const [, bump] = useState(0);
  useEffect(() => {
    const f = () => bump((n) => n + 1);
    window.addEventListener("vv-countdown-dismissed", f);
    return () => window.removeEventListener("vv-countdown-dismissed", f);
  }, []);
  if (!user || hidden || user.role === "admin" || user.is_family_member || user.promo_access) return null;
  if (pathname === "/explore" && countdownVisible(user)) return null;
  const price = user.role === "vendor" ? "$25/mo vendor plan" : user.family_plan ? "$19.99/mo Family plan" : "$9.99/mo";
  const s = bannerState(user, price);
  if (!s) return null;
  const addCard = async () => { setBusy(true); try { await (s.portal ? openBillingPortal() : startCheckout(user.checkout_plan)); } catch (e) { toast.error(errorMessage(e)); setBusy(false); } };

  return (
    <div data-testid="user-trial-banner"
      className={`text-sm px-4 py-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center ${s.tone === "alert" ? "bg-[#FF3B6B]" : "bg-[#00E5A3]/10 text-[#00E5A3]"}`}>
      <span data-testid="trial-banner-text">{s.text}</span>
      {s.action && (
        <button data-testid="trial-banner-add-card" disabled={busy} onClick={addCard} className="underline font-semibold">
          {busy ? "Opening…" : s.action}
        </button>
      )}
      {s.link && <Link to={s.link[0]} data-testid="trial-banner-upgrade" className="underline font-semibold">{s.link[1]}</Link>}
      {s.tone === "ok" && (
        <button data-testid="trial-banner-dismiss" onClick={() => setHidden(true)} aria-label="Dismiss">
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
